import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import WorkplaceCalendar from '../../../../Components/Calendar/WorkplaceCalendar';
import useCalendar from '../../Hooks/useFilter';
import { WorkplaceCalendarWithFilterDefaultValues } from './WorkplaceCalendarFilter';
// @ts-ignore
import styles from './WorkplaceCalendarWithFilter.module.scss';
import { useAppContext } from '../../../../Services/AppContext';
import WorkplaceCalendarToolbar from './WorkplaceCalendarToolbar';
import { Moment } from 'moment';
import { CalendarSettings } from '../../../../Components/Calendar/Calendar';
import useCustomSettings from '../../../Base/Hooks/useCustomSettings';
import { useEffectAsync } from '../../../../helpers';
import { CustomSettingKey } from '../../../Base/Enums/CustomSettingKey.enum';
import Loading from '../../../../Components/Loading';
import { CalendarViewIntervalEnum } from '../../Enums/CalendarViewInterval.enum';
import { CalendarView } from '../../Enums/CalendarView.eunm';
import { DEFAULT_SLOT_DURATION_OPTIONS } from '../../../Manager/Components/ManagerWrapper/Calendar/ManagerCalendarViewSettings/ManagerCalendarViewSettings';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import useTranslation from '../../../../Hooks/useTranslation';

interface Props {
	workplaceId: string;
	patientId?: string;
	params?: any;
}

const WorkplaceCalendarWithFilter: React.FC<Props> = ({
	workplaceId,
	patientId,
	params,
}) => {
	const { userData } = useAppContext();
	const { setFilter, filter } = useCalendar({ defaultFilter: params?.filter });
	const [filterDefaultValues, setFilterDefaultValues] = useState<
		WorkplaceCalendarWithFilterDefaultValues | undefined
	>(params?.filter);
	const wrapperRef = useRef<any>();
	const toolbarRef = useRef<any>();
	const [calendarHeight, setCalendarHeight] = useState<string>('100%');
	const [selectedDate, setSelectedDate] = useState<Moment | undefined>(
		undefined,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [settings, setSettings] = useState<CalendarSettings | undefined>(
		undefined,
	);
	const [slotDurationValue, setSlotDurationValue] = useState<
		SelectValue | undefined
	>(undefined);
  const { getCustomSettings } = useCustomSettings(workplaceId, userData.id);
	const { t } = useTranslation();

	const handleCalendarViewChange = (view: CalendarView) => {
		let newSlotDuration: SelectValue | undefined = undefined;
		if (view === CalendarView.TIME_GRID_DAY) {
			newSlotDuration = DEFAULT_SLOT_DURATION_OPTIONS.find(
				item => item.value === settings?.slotDurationDay,
			);
		} else if (view === CalendarView.TIME_GRID_WEEK) {
			newSlotDuration = DEFAULT_SLOT_DURATION_OPTIONS.find(
				item => item.value === settings?.slotDurationWeek,
			);
		}
		if (newSlotDuration) {
			setSlotDurationValue({
				value: newSlotDuration?.value,
				label: t(newSlotDuration?.label),
			});
		}
	};

	const handleOnShowOnyMeChange = () => {
		setFilterDefaultValues({
			workplaceId,
			doctorIds: [
				{
					value: userData.id,
				},
			],
		});
	};

	const handleCalculateDimensions = () => {
		setTimeout(() => {
			if (wrapperRef.current && toolbarRef.current) {
				const wrapperHeight = wrapperRef.current.clientHeight;
				const filterHeight = toolbarRef.current.clientHeight;
				setCalendarHeight(`${wrapperHeight - filterHeight}px`);
			}
		}, 100);
	};

	useEffect(() => {
		setFilterDefaultValues({ workplaceId });
	}, []);

	useLayoutEffect(() => {
		setTimeout(() => {
			handleCalculateDimensions();
		}, 1000);
	}, []);

	useEffect(() => {
		// @ts-ignore
		window.addEventListener('resize', handleCalculateDimensions);
		return () => {
			// @ts-ignore
			window.removeEventListener('resize', handleCalculateDimensions);
		};
	}, []);

	const handleSetDate = (date: Moment) => {
		setSelectedDate(date);
	};

  const handleSlotDurationChange = (slotDuration: CalendarViewIntervalEnum) => {
    setSettings(prev => {
      return {
        ...prev,
        slotDurationDay: slotDuration,
        slotDurationWeek: slotDuration,
      };
    });
  };

	const handleLoadCustomSettings = async () => {
		setIsLoading(true);
		const settings = await getCustomSettings(
			CustomSettingKey.CALENDAR_SETTINGS,
		);

		const formatHour = (hourStr?: string): string | undefined => {
			if (!hourStr) {
				return undefined;
			}
			const hour = parseInt(hourStr, 10);
			const paddedHour = hour.toString().padStart(2, '0');
			return `${paddedHour}:00:00`;
		};

		if (!settings) {
			return;
		}

		setSettings({
			showWeekends: settings.showWeekends,
			highlightWorkingTime: settings.highlightWorkingTime,
			slotMinTime: formatHour(settings.calendarViewTime?.showCalendarFrom),
			slotMaxTime: formatHour(settings.calendarViewTime?.showCalendarTo),
			initialView: settings.defaultView?.value,
			forceMinimumEventHeight: settings.forceMinimumEventHeight,
			slotDurationDay: settings?.defaultSlotDurationDay?.value,
			slotDurationWeek: settings?.defaultSlotDurationWeek?.value,
		});
		setIsLoading(false);
	};

	useEffectAsync(async () => {
		await handleLoadCustomSettings();
	}, []);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper} ref={wrapperRef}>
			<div ref={toolbarRef}>
				<WorkplaceCalendarToolbar
					setFilter={setFilter}
					filterDefaultValues={filterDefaultValues}
					onDateSelect={handleSetDate}
					onShowOnlyMe={handleOnShowOnyMeChange}
					workplaceId={workplaceId}
					filter={filter}
					onToolbarToggle={handleCalculateDimensions}
					onSlotDurationChange={handleSlotDurationChange}
					slotDurationValue={slotDurationValue}
					onCalendarSettingsChange={handleLoadCustomSettings}
				/>
			</div>
			<div style={{ height: calendarHeight, position: 'relative' }}>
				<WorkplaceCalendar
					workplaceId={workplaceId}
					filter={filter}
					params={params}
					patientId={patientId}
					preselectedDate={selectedDate}
					settings={settings}
					onViewChange={handleCalendarViewChange}
				/>
			</div>
		</div>
	);
};

export default WorkplaceCalendarWithFilter;
